import { useEffect, useRef } from 'react';
import { calculateDistance } from '../components/Dispatch/LiveDispatch/locationHelper';

/**
 * GPS geofence auto-arrive (spec: 02-trip-lifecycle.md "GPS geofence arrival").
 *
 * While en route to a pickup, once the vehicle's GPS position stays inside the
 * configured radius around the pickup for `debounceMs` continuously, fires
 * `onArrive` once. Guards:
 *  - Requires `isAccurate` (mirrors GeolocationHandler's accuracy<=100m gate) —
 *    poor accuracy never auto-advances; the driver falls back to the manual
 *    swipe/tap "Confirm Arrived" action.
 *  - Debounce requires *continuous* dwell inside the radius, not a single fix,
 *    so a brief drive-past near the pickup doesn't false-trigger. A fix that
 *    lands just outside the radius (up to 1.5x) doesn't reset the dwell timer
 *    by itself — only a clear move-away does — to tolerate normal GPS jitter.
 *  - Fires at most once per `resetKey` (pass the waypoint's job number so a
 *    new trip/waypoint gets a fresh chance to fire).
 *
 * This only decides *when* to call onArrive — the caller wires onArrive to the
 * exact same action the manual "Confirm Arrived" control uses
 * (onWaypointAction('arrive', ...)), so there's no separate arrival code path.
 */
export default function useGeofenceArrival({
  enabled,
  center, // { lat, lng } current vehicle position (LiveDispatch's throttled GPS state)
  isAccurate,
  targetLocation, // [lat, lng] — waypoint.location
  resetKey,
  radiusMeters = 150,
  debounceMs = 8000,
  onArrive,
}) {
  const insideSinceRef = useRef(null);
  const firedRef = useRef(false);
  const lastResetKeyRef = useRef(resetKey);

  useEffect(() => {
    if (lastResetKeyRef.current !== resetKey) {
      lastResetKeyRef.current = resetKey;
      insideSinceRef.current = null;
      firedRef.current = false;
    }
  }, [resetKey]);

  useEffect(() => {
    if (!enabled) {
      insideSinceRef.current = null;
      firedRef.current = false;
      return;
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled || firedRef.current) return;

    if (!isAccurate) {
      // Don't let a stale "inside" dwell survive a stretch of poor accuracy.
      insideSinceRef.current = null;
      return;
    }

    const lat2 = Array.isArray(targetLocation) ? Number(targetLocation[0]) : NaN;
    const lng2 = Array.isArray(targetLocation) ? Number(targetLocation[1]) : NaN;
    const lat1 = Number(center?.lat);
    const lng1 = Number(center?.lng);
    if (![lat1, lng1, lat2, lng2].every(Number.isFinite)) return;
    if (lat2 === 0 && lng2 === 0) return; // unset/placeholder waypoint location

    const distance = calculateDistance(lat1, lng1, lat2, lng2);
    const now = Date.now();

    if (distance <= radiusMeters) {
      if (!insideSinceRef.current) {
        insideSinceRef.current = now;
      } else if (now - insideSinceRef.current >= debounceMs) {
        firedRef.current = true;
        onArrive?.();
      }
    } else if (distance > radiusMeters * 1.5) {
      insideSinceRef.current = null;
    }
  }, [enabled, center, isAccurate, targetLocation, radiusMeters, debounceMs, onArrive]);
}
